/**
 * The two platen reads product sync makes (Phase 86).
 *
 * `usePlatenSizeContext(productId)` is ONE `productSync.getPlatenSizeContext`
 * query per product (60-second stale time): the org's rules plus only THIS
 * product's overrides. An unscoped read caps overrides at a bounded number of
 * products (`overridesTruncated`), so a product past the cap would silently
 * group by the rules alone; scoping by product never truncates. It is never
 * asked for a product whose type the org read (`useOrgPlatenContext`, the
 * unscoped read, whose RULES are complete) says is unruled. The server
 * throws on a read failure rather than answering "no rules", so callers render
 * `isError` as an error, never as a product without rules. Any override write
 * invalidates every variant of the query at once.
 *
 * Round 4: both answers also carry `pixels`, the rules org's Platen variants
 * (each platen's Large px and Made from Large preset; the 3PL's for a
 * supply product with its consent), so a surface reads them from the read it
 * already makes.
 *
 * `usePlatenFileState` reads the per-SKU platen file markers and artwork link
 * flags for one ruled product's synced SKUs, one `productSync.getPlatenFileState` query per
 * stable 30-SKU chunk (`platenFileStateChunks`), all chunks in parallel. The
 * same SKUs always make the same query keys, so another surface asking about
 * the same product reuses the cached chunks. Disabled means no request.
 */
import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';
import { platenFileStateChunks, platenFileStateFromRow, type PlatenFileStateBySku } from '../lib/platen';

export const PLATEN_READ_STALE_MS = 60_000;

const PLATEN_COMMIT_REFUSED_TOAST_ID = 'platen-commit-refused';

/**
 * One product's platen context (the org's rules plus this product's
 * overrides). `enabled: false` makes no request: a row whose type the org
 * read says is unruled never asks.
 *
 * Phase 92 (92-18, 92-27b; D-09): `opts.supply` is the page's hint that this
 * product is built on a 3PL's blank (from the scan summary it already has).
 * The server then answers the 3PL's rules, but only when the caller's own
 * saved choice for the product really is supply. Without it, the input and
 * the query key are exactly today's (no `supply` key at all).
 */
export function usePlatenSizeContext(shopifyProductId: string, enabled = true, opts?: { supply?: boolean }) {
  const input = opts?.supply ? { shopifyProductId, supply: true } : { shopifyProductId };
  return trpc.productSync.getPlatenSizeContext.useQuery(input, { staleTime: PLATEN_READ_STALE_MS, enabled });
}

/**
 * The ORG's platen context: the same procedure, unscoped. Its rules are
 * always complete (only the overrides are bounded), so it answers "does this
 * org have platen size rules" and "is this product's type ruled" for every
 * product at once. Product sync starts it when the page mounts (batched into
 * the page's first request), so the Product updates tab and every expanded
 * row read it from the cache. The server answers it from the same 5-minute
 * platen context cache print time uses, so for an org without rules it is
 * normally no database read at all.
 *
 * A row passes `refetchOnMount: false`: it reads what the page holds and
 * never refetches on expand. The page's own observer keeps it fresh, and
 * every platen write invalidates it.
 */
export function useOrgPlatenContext(options: { refetchOnMount?: boolean } = {}) {
  return trpc.productSync.getPlatenSizeContext.useQuery(undefined, {
    staleTime: PLATEN_READ_STALE_MS,
    ...options,
  });
}

export interface PlatenFileStateResult {
  bySku: PlatenFileStateBySku;
  isLoading: boolean;
  isError: boolean;
  /**
   * BLU-1238 (R3-2): any of these SKUs says its product keeps printing its
   * current files (an active skip platen mark). Read from the same rows, no
   * extra request.
   */
  keepsCurrentFiles: boolean;
}

/**
 * The stable chunks for `skus`, keyed on their CONTENT, so a caller that
 * builds a new array with the same SKUs every render keeps the same chunks
 * (and so the same memoised map and callbacks).
 */
function useStableChunks(skus: readonly string[], enabled = true): string[][] {
  const key = enabled ? JSON.stringify(platenFileStateChunks(skus)) : '[]';
  return useMemo(() => JSON.parse(key) as string[][], [key]);
}

/**
 * Round 4: `opts.includeMadeFromLarge` also asks which Small sides were made
 * from Large (one marker read beside the RPC, server side). Sent only when
 * true, so every other caller's input and query keys are exactly today's.
 * `usePlatenFileStateRefresh` invalidates by `{ skus }`, which matches both.
 */
export function usePlatenFileState(
  skus: readonly string[],
  enabled: boolean,
  opts?: { includeMadeFromLarge?: boolean },
): PlatenFileStateResult {
  const chunks = useStableChunks(skus, enabled);
  const includeMade = opts?.includeMadeFromLarge === true;
  const queries = trpc.useQueries((tq) =>
    chunks.map((chunk) =>
      tq.productSync.getPlatenFileState(
        includeMade ? { skus: chunk, includeMadeFromLarge: true } : { skus: chunk },
        { enabled, staleTime: PLATEN_READ_STALE_MS },
      ),
    ),
  );

  // Stable between renders: the map is rebuilt only when a chunk's data changes
  // (each query's `dataUpdatedAt`) or the chunk list does, so the grid's
  // derived values and every cell's effects do not re-run on unrelated
  // renders. `queries` itself is a new array every render.
  const dataKey = queries.map((q) => q.dataUpdatedAt).join(',');
  const { bySku, keepsCurrentFiles } = useMemo(() => {
    const out: PlatenFileStateBySku = {};
    let kept = false;
    for (const query of queries) {
      for (const row of query.data ?? []) {
        out[row.sku_code] = platenFileStateFromRow(row);
        if (row.platenSkipped === true) kept = true;
      }
    }
    return { bySku: out, keepsCurrentFiles: kept };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `dataKey` + `chunks` track every chunk's data.
  }, [chunks, dataKey]);

  return {
    bySku,
    keepsCurrentFiles: enabled && keepsCurrentFiles,
    isLoading: enabled && queries.some((q) => q.isLoading),
    isError: enabled && queries.some((q) => q.isError),
  };
}

/**
 * The two refreshes every platen upload surface needs, for one product's SKUs:
 * `invalidateFileState` refetches exactly this product's cached file-state
 * chunks (the same stable chunks the read used), and `onPlatenCommitFailed`
 * handles a refused commit: the rules may have changed under the page, so it
 * re-reads the config and the file state and shows the server's message
 * persistently (DESIGN.md section 15.5).
 */
export function usePlatenFileStateRefresh(skus: readonly string[]) {
  const utils = trpc.useUtils();
  const chunks = useStableChunks(skus);
  // Resolves once the active reads refetched (86-34: the derive queue waits
  // for it before a cell shows made or kept).
  const invalidateFileState = useCallback(
    (): Promise<unknown> =>
      Promise.all(chunks.map((chunk) => utils.productSync.getPlatenFileState.invalidate({ skus: chunk }))),
    [utils, chunks],
  );
  const onPlatenCommitFailed = useCallback(
    (message: string) => {
      void utils.productSync.getPlatenSizeContext.invalidate();
      void invalidateFileState();
      // A fixed id: several cells refused by the same rule change show ONE
      // persistent toast, not one each.
      toast.error(message, { id: PLATEN_COMMIT_REFUSED_TOAST_ID, duration: Infinity });
    },
    [utils, invalidateFileState],
  );
  return { invalidateFileState, onPlatenCommitFailed };
}
